import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  uuid,
  commonUtils,
  useNotifications,
  useEditorStore,
  useFlowBuilder2AddFlowStepMutation,
  Events,
} from '@diana/core';
import { FLOW_BUILDER2_PAGE_NS } from '../i18n';
import { useFlowBuilder2PageStore } from '../Store/useFlowBuilder2PageStore';
import { scrollToStep } from './useBuilderScroll';

export const useAddTask = (options?: Parameters<typeof useFlowBuilder2AddFlowStepMutation>[0]) => {
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const notifications = useNotifications();

  const setActiveTaskId = useFlowBuilder2PageStore((s) => s.setActiveTaskId);

  const getFlow = useEditorStore((s) => s.getFlow);
  const updateFlow = useEditorStore((s) => s.updateFlow);

  const { mutateAsync, isPending: isAddingTask } = useFlowBuilder2AddFlowStepMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      updateFlow((flow) => {
        const prevIds = new Set(flow.steps.map((s) => s.id));
        const newItem = data.find((s) => !prevIds.has(s.id));

        if (newItem) {
          setActiveTaskId(newItem.taskId);
          setTimeout(() => scrollToStep(newItem.taskId));
        }

        return { ...flow, steps: data };
      });
      Events.ToggleFlowBuilderNavStepNode.post({ value: true });
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      notifications.error({
        message: t('errors.addTaskFailed'),
        description: commonUtils.getErrorMessage(error),
      });
      options?.onError?.(error, variables, context);
    },
  });

  const addTask = useCallback(
    async (stepId: string) => {
      const flow = getFlow();
      if (!flow) throw new Error('Flow is missing');

      const step = flow.steps.find((step) => step.stepId === stepId);
      if (!step) throw new Error('Step is missing');

      return mutateAsync({
        flowId: flow.id,
        position: -1,
        data: {
          stepId: step.stepId,
          stepName: step.stepName,
          taskName: `NoCodeTask-${uuid()}`,
          actionSetName: `NoCodeActionSet-${uuid()}`,
        },
      });
    },
    [getFlow, mutateAsync],
  );

  return { addTask, isAddingTask };
};
